/**
 * 「今日の県」。
 *
 * 毎日1県が決まり、その日は**全員が同じ順番で料理が落ちてくる**（乱数の種を日付と県で固定）。
 * 同じ条件で遊ぶので、スコアを見せ合える。連続日数で「また明日」の理由を作る。
 *
 * - 日付は端末の現地時間（日本の利用者なら日本時間）
 * - `/fukuoka?daily=2026-09-24` の形で入る。日付が今日でなければ、ふつうのプレイになる
 */

import { metaStorageKey } from '../game/config/gameConfig';
import type { StorageLike } from '../game/systems/StorageSystem';
import { JsonStore } from './jsonStore';
import { localDateKey } from './wallet';

export { localDateKey };

export { dailyRegionFor, hashString, seededRandom } from './dailyCore';

export function dailySeed(dateKey: string, regionId: string): string {
  return `daily:${dateKey}:${regionId}`;
}

function previousDateKey(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  return localDateKey(new Date(y, m - 1, d - 1));
}

export interface DailyRecord {
  /** 最後に今日の県を遊んだ日。 */
  lastDate: string;
  /** その日のベストスコア（コンティニュー・おはしを使ったプレイも含む）。 */
  bestToday: number;
  /** その日のベストが、コンティニュー・おはしを使ったプレイの記録か（項目の無い古いデータは false）。 */
  bestTodayAssisted: boolean;
  /**
   * その日の、コンティニューもおはしも使わなかったプレイのベスト（ランキングに出る方）。
   * 項目の無い古いデータは bestToday と同じ値にする。
   */
  bestTodayNoAssist: number;
  /** その日に遊んだ回数。 */
  playsToday: number;
  /** 連続日数。 */
  streak: number;
  bestStreak: number;
  /** 今日の県を遊んだ日数の合計。 */
  totalDays: number;
  /** おやすみ券を最後に使った日（週に1回まで）。 */
  restUsedOn: string;
  /** 連続日数のごほうび（7日ごとにおはし1本）を最後に受け取った日。 */
  streakRewardOn: string;
}

/** 連続日数のごほうびを出す間隔（日）。7日・14日・21日…で1本。 */
export const STREAK_REWARD_EVERY = 7;

/** おやすみ券を使える間隔（日）。 */
export const REST_INTERVAL_DAYS = 7;

function daysBetween(from: string, to: string): number {
  const [y1, m1, d1] = from.split('-').map(Number);
  const [y2, m2, d2] = to.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000);
}

function count(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function normalizeDaily(raw: unknown): DailyRecord {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const bestToday = count(r.bestToday);
  return {
    lastDate: typeof r.lastDate === 'string' ? r.lastDate : '',
    bestToday,
    bestTodayAssisted: bestToday > 0 && r.bestTodayAssisted === true,
    bestTodayNoAssist: Math.min(
      bestToday,
      r.bestTodayNoAssist === undefined ? bestToday : count(r.bestTodayNoAssist),
    ),
    playsToday: count(r.playsToday),
    streak: count(r.streak),
    bestStreak: count(r.bestStreak),
    totalDays: count(r.totalDays),
    restUsedOn: typeof r.restUsedOn === 'string' ? r.restUsedOn : '',
    streakRewardOn: typeof r.streakRewardOn === 'string' ? r.streakRewardOn : '',
  };
}

export class DailyBook {
  private readonly store: JsonStore<DailyRecord>;

  constructor(storage?: StorageLike | null) {
    this.store = new JsonStore(metaStorageKey('daily'), normalizeDaily, storage);
  }

  /** いまの記録（今日まだ遊んでいなければ、今日の数は0、連続は昨日までで数える）。 */
  view(now: Date = new Date()): DailyRecord & { playedToday: boolean; streakAlive: boolean } {
    const d = this.store.data;
    const today = localDateKey(now);
    const playedToday = d.lastDate === today;
    const streakAlive = playedToday || d.lastDate === previousDateKey(today);
    return {
      ...d,
      bestToday: playedToday ? d.bestToday : 0,
      bestTodayAssisted: playedToday ? d.bestTodayAssisted : false,
      bestTodayNoAssist: playedToday ? d.bestTodayNoAssist : 0,
      playsToday: playedToday ? d.playsToday : 0,
      streak: streakAlive ? d.streak : 0,
      playedToday,
      streakAlive,
    };
  }

  /**
   * おやすみ券が使えるか。
   * 昨日だけ遊び忘れて連続（2日以上）が切れかけていて、今週まだ使っていないとき。
   * 使うと「昨日も遊んだ」扱いになり、今日遊べば連続がつながる。
   */
  canRest(now: Date = new Date()): boolean {
    const d = this.store.data;
    const today = localDateKey(now);
    if (d.streak < 2 || !d.lastDate) return false;
    if (daysBetween(d.lastDate, today) !== 2) return false;
    if (d.restUsedOn && daysBetween(d.restUsedOn, today) < REST_INTERVAL_DAYS) return false;
    return true;
  }

  /** おやすみ券を使う。使えたら true。 */
  useRest(now: Date = new Date()): boolean {
    if (!this.canRest(now)) return false;
    const today = localDateKey(now);
    this.store.update((d) => {
      d.lastDate = previousDateKey(today);
      d.bestToday = 0;
      d.bestTodayAssisted = false;
      d.bestTodayNoAssist = 0;
      d.playsToday = 0;
      d.restUsedOn = today;
    });
    return true;
  }

  /**
   * 連続日数のごほうび。今日の県を遊んだ（record のあと）連続日数が 7 の倍数の日に、1日1回だけ true。
   * 受け取ったら、その日はもう出ない。
   */
  claimStreakReward(now: Date = new Date()): boolean {
    const d = this.store.data;
    const today = localDateKey(now);
    if (d.lastDate !== today) return false;
    if (d.streak <= 0 || d.streak % STREAK_REWARD_EVERY !== 0) return false;
    if (d.streakRewardOn === today) return false;
    this.store.update((draft) => {
      draft.streakRewardOn = today;
    });
    return true;
  }

  /**
   * 1プレイ分を記録する。今日のベストを更新したら true。
   * `assisted`（コンティニュー・おはしを使ったプレイ）はランキングに入らないので、
   * 使わなかったプレイのベスト（bestTodayNoAssist）を別に覚えておく。
   */
  record(score: number, now: Date = new Date(), options: { assisted?: boolean } = {}): boolean {
    const assisted = options.assisted === true;
    const today = localDateKey(now);
    let improved = false;
    this.store.update((d) => {
      if (d.lastDate !== today) {
        d.streak = d.lastDate === previousDateKey(today) ? d.streak + 1 : 1;
        d.bestStreak = Math.max(d.bestStreak, d.streak);
        d.totalDays += 1;
        d.bestToday = 0;
        d.bestTodayAssisted = false;
        d.bestTodayNoAssist = 0;
        d.playsToday = 0;
        d.lastDate = today;
      }
      d.playsToday += 1;
      if (score > d.bestToday) {
        d.bestToday = Math.floor(score);
        d.bestTodayAssisted = assisted;
        improved = true;
      }
      if (!assisted && score > d.bestTodayNoAssist) {
        d.bestTodayNoAssist = Math.floor(score);
      }
    });
    return improved;
  }
}
